import * as Linking from "expo-linking";
import type { LinkingOptions } from "@react-navigation/native/lib/typescript/src/types";
import Routes from "@navigations/Routes";
import {
  NotificationClickEvent,
  NotificationWillDisplayEvent,
  OneSignal,
} from "react-native-onesignal";
import { showToast, ToastTypes } from "@components/molecules/VDBToast/util";
import { QueryClient } from "@tanstack/react-query";
import handleRouteChange from "@utils/deeplink-invalidations";
import { REQUEST_ACTIONS, NotificationType, ViewType } from "@utils/constants";
import { DeviceEventEmitter, Platform } from "react-native";
import { getPathFromState, getStateFromPath } from "@react-navigation/core";
import { navigationRef } from "@navigations/utils/RootNavigation";
import { rewriteDeeplink } from "./rewriteDeeplink";
import { resolveVanitySlug, reverseLookupVanitySlug } from "./vanityUrls";

// TODO: React navigation v7 handles this better, we need to change this when we will update
const prefix = Platform.OS === "web" ? "/webapp/" : "";
const appScheme = process.env.EXPO_PUBLIC_APP_SCHEME;
const prefixes = [
  `${appScheme}://`,
  process.env.EXPO_PUBLIC_APP_URL
    ? `${process.env.EXPO_PUBLIC_APP_URL}/webapp`
    : "",
];

if (Platform.OS === "web" && typeof window !== "undefined") {
  try {
    const urlProductId =
      window.location.pathname.match(/item-detail\/(\d+)/)?.[1];
    const storedItem = JSON.parse(localStorage.getItem("itemDetail") || "null");
    if (
      urlProductId &&
      storedItem?.id != null &&
      String(storedItem.id) !== urlProductId
    ) {
      localStorage.removeItem("jcSelectedOption");
      localStorage.removeItem("personalization");
      localStorage.removeItem("itemDetail");
    }
  } catch {}
}

interface AdditionalData {
  redirect_link?: string;
  type?: string;
  view_type?: string;
  action?: string;
}

const webPrefixPath = Platform.OS === "web" ? "/webapp" : "";

const VANITY_PATH_RE = /^\/(?:webapp\/)?search\/result\/([^/?#]+)(\?[^#]*)?$/;
const SEARCH_RESULT_PATH_RE = /^\/(?:webapp\/)?[^/]+\/search\/?(?:\?|$)/;

const expandVanityPath = (path: string): string | null => {
  try {
    const match = path.match(VANITY_PATH_RE);
    if (!match) return null;
    const [, slug, queryStr = ""] = match;
    const resolved = resolveVanitySlug(slug);
    if (!resolved) return null;

    const search = new URLSearchParams();
    search.set("productType", resolved.productType);
    if (resolved.priceMode !== undefined) {
      search.set("priceMode", String(resolved.priceMode));
    }
    search.set("saved_search", resolved.savedSearch);
    if (resolved.sectionName) {
      search.set("sectionName", resolved.sectionName);
    }
    const userQuery = new URLSearchParams(queryStr.replace(/^\?/, ""));
    userQuery.forEach((value, key) => search.set(key, value));

    return `${webPrefixPath}/${
      resolved.productPrefix
    }/search?${search.toString()}`;
  } catch {
    return null;
  }
};

const findFocusedRoute = (state: any): any => {
  if (!state?.routes?.length) return undefined;
  const index =
    typeof state.index === "number" ? state.index : state.routes.length - 1;
  const route = state.routes[index];
  if (route?.state) return findFocusedRoute(route.state);
  return route;
};

const SEARCH_ROUTE_NAMES = new Set([
  Routes.SearchResultScreen,
  Routes.FilterSplitScreen,
  Routes.FilterScreen,
]);

const searchIdentity = (params: any) =>
  [
    params?.productType ?? "",
    params?.productPrefix ?? "",
    params?.sectionName ?? "",
    params?.saved_search ?? params?.new_search ?? "",
  ].join("|");

const routesMatch = (route: any, target: any) => {
  if (!route || !target || route.name !== target.name) return false;
  const routeId = route.params?.productID ?? route.params?.itemId;
  const targetId = target.params?.productID ?? target.params?.itemId;
  if (routeId != null && targetId != null) {
    return String(routeId) === String(targetId);
  }
  if (SEARCH_ROUTE_NAMES.has(route.name)) {
    return searchIdentity(route.params) === searchIdentity(target.params);
  }
  return true;
};

const reuseLiveStackForHistory = (parsedState: any) => {
  if (Platform.OS !== "web" || !parsedState || !navigationRef.isReady()) {
    return parsedState;
  }

  let currentState: any;
  try {
    currentState = navigationRef.getRootState();
  } catch {
    return parsedState;
  }

  const parsedFocused = findFocusedRoute(parsedState);
  const currentFocused = findFocusedRoute(currentState);
  if (!parsedFocused || !currentFocused) return parsedState;
  if (routesMatch(currentFocused, parsedFocused)) return parsedState;

  const focusedIndex =
    typeof currentState.index === "number"
      ? currentState.index
      : currentState.routes.length - 1;
  const trimmed = trimStateToRoute(
    currentState,
    parsedFocused,
    focusedIndex > 0 ? focusedIndex - 1 : focusedIndex
  );
  return trimmed ?? parsedState;
};

const routeOrder = (length: number, preferIndex?: number) => {
  const order: number[] = [];
  if (
    preferIndex != null &&
    preferIndex >= 0 &&
    preferIndex < length &&
    !order.includes(preferIndex)
  ) {
    order.push(preferIndex);
  }
  for (let i = 0; i < length; i++) {
    if (!order.includes(i)) order.push(i);
  }
  return order;
};

const trimStateToRoute = (
  state: any,
  target: any,
  preferIndex?: number
): any | null => {
  if (!state?.routes?.length) return null;
  const isStack = state.type === "stack" || state.type == null;
  const order = routeOrder(state.routes.length, preferIndex);

  for (const i of order) {
    if (!routesMatch(state.routes[i], target)) continue;
    if (!isStack) return { ...state, index: i };
    return {
      ...state,
      index: i,
      routes: state.routes.slice(0, i + 1),
    };
  }

  for (const i of order) {
    const route = state.routes[i];
    if (!route?.state) continue;
    const nestedIndex =
      typeof route.state.index === "number" ? route.state.index : undefined;
    const nested = trimStateToRoute(route.state, target, nestedIndex);
    if (!nested) continue;

    const routes = isStack
      ? state.routes.slice(0, i + 1)
      : state.routes.slice();
    routes[i] = { ...routes[i], state: nested };
    return { ...state, index: i, routes };
  }

  return null;
};

const setFocusedRoutePath = (state: any, path: string): void => {
  let current = state;
  while (current?.routes?.length) {
    const index =
      typeof current.index === "number"
        ? current.index
        : current.routes.length - 1;
    const route = current.routes[index];
    if (!route) return;
    if (route.state) {
      current = route.state;
    } else {
      route.path = path;
      return;
    }
  }
};

const shortenSearchResultPath = (path: string): string | null => {
  try {
    if (!SEARCH_RESULT_PATH_RE.test(path)) return null;
    const [pathnameOnly, queryStr] = path.split("?");
    if (!pathnameOnly || !queryStr) return null;

    const params = new URLSearchParams(queryStr);
    const productType = params.get("productType");
    if (!productType) return null;
    const savedSearchRaw = params.get("saved_search");
    if (!savedSearchRaw) return null;

    let parsed: Record<string, unknown> = {};
    try {
      parsed = JSON.parse(savedSearchRaw);
    } catch {
      return null;
    }
    const sectionName = params.get("sectionName") ?? undefined;
    const slug = reverseLookupVanitySlug(productType, parsed, sectionName);
    if (!slug) return null;

    const extras = new URLSearchParams();
    params.forEach((value, key) => {
      if (
        key === "productType" ||
        key === "saved_search" ||
        key === "sectionName"
      ) {
        return;
      }
      extras.set(key, value);
    });
    const extrasStr = extras.toString();
    const shortPath = `${webPrefixPath}/search/result/${slug}`;
    return extrasStr ? `${shortPath}?${extrasStr}` : shortPath;
  } catch {
    return null;
  }
};

const getNotificationMessage = (
  t: any,
  type?: string,
  view_type?: string,
  action?: string
) => {
  if (type === NotificationType.MESSAGE) {
    return t("inApp.message.text");
  }
  if (type === NotificationType.ORDER_CART_APPROVED) {
    return t("inApp.orderCartApproved.text", "Your order has been approved");
  }
  if (type !== "request" || !view_type || !action) return "";

  if (view_type === ViewType.BUYER) {
    switch (action) {
      case REQUEST_ACTIONS.ACCEPTED:
        return t("inApp.buyer.accepted.text");
      case REQUEST_ACTIONS.DECLINE:
        return t("inApp.buyer.decline.text");
      case REQUEST_ACTIONS.EXPIRED:
        return t("inApp.buyer.expired.text");
      default:
        return "";
    }
  }

  if (view_type === ViewType.SELLER) {
    switch (action) {
      case REQUEST_ACTIONS.NEW:
        return t("inApp.seller.new.text");
      case REQUEST_ACTIONS.MODIFIED:
        return t("inApp.seller.modified.text");
      case REQUEST_ACTIONS.WITHDRAW:
        return t("inApp.seller.withdraw.text");
      case REQUEST_ACTIONS.EXPIRED:
        return t("inApp.seller.expired.text");
      default:
        return "";
    }
  }

  return "";
};

const getLinking = (
  client: QueryClient,
  t: any,
  isMobile?: boolean,
  exploreNavigatorInitialRouteName = Routes.Explore,
  isLargeScreen?: boolean,
  hasSingleAllowedProductType?: boolean
): LinkingOptions<any> => ({
  prefixes: prefixes,
  config: {
    screens: {
      [Routes.BulkRequestNavigator]: {
        screens: {
          [Routes.OrderCartScreen]: {
            path: `${prefix}order-cart`,
          },
          [Routes.BulkRequestScreen]: {
            path: `${prefix}bulk-request`,
          },
          [Routes.BulkInitScreen]: {
            path: `${prefix}bulk-request-navigator/bulk-init-screen`,
          },
        },
      },
      [Routes.AppNavigator]: {
        screens: {
          [Routes.FavoritesNavigator]: {
            screens: {
              [Routes.Favorites]: {
                path: `${prefix}collections`,
              },
              [Routes.FavoritesListScreen]: {
                path: `${prefix}collections/:collectionId?`,
              },
            },
          },
          [Routes.RequestHistoryNavigator]: {
            initialRouteName: Routes.RequestHistory,
            screens: {
              [Routes.RequestDetailScreen]: {
                path: `${prefix}/requests/:id`,
              },

              [isMobile ? Routes.RequestHistory : Routes.ChooseARequestScreen]:
                {
                  path: `${prefix}requests`,
                },
            },
          },
          [Routes.HomeNavigator]: {
            screens: {
              [Routes.HomeScreen]: `${prefix}home`,
            },
          },
          [Routes.PagesNavigator]: {
            screens: {
              [Routes.PagesScreen]: `${prefix}pages/:id`,
            },
          },
          [Routes.MoreNavigator]: {
            screens: {
              [Routes.TermsOfUse]: `${prefix}terms`,
              ...(!isLargeScreen
                ? {
                    [isMobile
                      ? Routes.AccountOverviewUsersSeats
                      : Routes.AccountOverviewScreen]: `${prefix}account-overview-screen`,
                  }
                : {}),
            },
          },
          [Routes.ExploreNavigator]: {
            initialRouteName:
              isLargeScreen && hasSingleAllowedProductType
                ? exploreNavigatorInitialRouteName
                : Routes.Explore,
            screens: {
              [isMobile ? Routes.SearchResultScreen : Routes.FilterSplitScreen]:
                {
                  path: `${prefix}:productPrefix/search`,
                },
              [Routes.PDPScreen]: {
                path: `${prefix}/:productPrefix/item-detail/:productID`,
              },
              [Routes.FindMatchScreen]: {
                path: `${prefix}/:productPrefix/find-a-match/:itemId`,
              },
              [Routes.SearchText]: {
                path: `${prefix}:productType/:priceMode/new-search/new`,
              },
              [Routes.Explore]: {
                path: `${prefix}explore`,
              },

              [Routes.SavedSearch]: {
                path: `${prefix}saved-searches`,
              },
              ...(isLargeScreen
                ? {
                    [Routes.AccountOverviewScreen]: `${prefix}account-overview-screen`,
                  }
                : {}),
              [Routes.FilterScreen]: {
                path: `${prefix}/:productPrefix/search/new`,
              },
              [Routes.ReviewPage]: {
                path: `${prefix}jewelry-builder/review`,
              },
            },
          },
        },
      },
      [Routes.GlobalNavigator]: {
        screens: {
          [Routes.PDPScreen]: `${prefix}/:productPrefix/item-detail/:productID/global`,
          [Routes.FindMatchScreen]: `${prefix}/:productPrefix/find-a-match/:itemId/global`,
          [Routes.RequestDetailScreen]: `${prefix}/requests/:id/global`,
          [Routes.FilterSplitScreen]: `${prefix}:productPrefix/search/global`,
          [Routes.SearchResultScreen]: `${prefix}:productPrefix/search-result/global`,
          [Routes.ReviewPage]: `${prefix}jewelry-builder/review/global`,
        },
      },
      [Routes.AuthNavigator]: {
        screens: {
          [Routes.Register]: `${prefix}register`,
          [Routes.PasswordUpdate]: `${prefix}user/password`,
          [Routes.VerifiedScreen]: `${prefix}user/confirmation`,
          [Routes.ForgotPassword]: `${prefix}forgot-password/:username`,
          [isMobile
            ? Routes.Authentication
            : Routes.OnboardingWeb]: `${prefix}login`,
        },
      },
      [Routes.PostOnBoardingScreen]: {
        path: `${prefix}post-onboarding/:isPostOnboarding?`,
        parse: {
          isPostOnboarding: (isPostOnboarding: string) =>
            isPostOnboarding === "true",
        },
      },
    },
  },
  getInitialURL: async () => {
    const rawUrl = await Linking.getInitialURL();
    const rewritten = rawUrl ? rewriteDeeplink(rawUrl) : null;
    return rewritten;
  },
  subscribe: (listener) => {
    const urlSubscription = Linking.addEventListener("url", ({ url }) => {
      const rewritten = rewriteDeeplink(url);
      if (rewritten.includes("/item-detail/")) {
        DeviceEventEmitter.emit("CLEAR_JEWELRY_CONTEXT");
      }
      if (rewritten.includes("item_detail")) {
        listener(`${rewritten}?fromDeepLink=true`);
      } else {
        listener(rewritten);
      }
    });

    const handleBackgroundNotification = (
      notification: NotificationClickEvent
    ) => {
      let link = (notification?.notification?.additionalData as AdditionalData)
        ?.redirect_link;

      if (link === "post-onboarding") {
        link = "post-onboarding/true";
      }

      if (link) {
        // const fullLink = `${appScheme}://${link}`; //commented out as appScheme will be appended in rewriteDeeplink function
        handleRouteChange(client, link);
        listener(rewriteDeeplink(link));
      }
    };

    const handleForegroundNotification = (
      notificationReceivedEvent: NotificationWillDisplayEvent
    ) => {
      let notification = notificationReceivedEvent.getNotification();

      let link = (notification?.additionalData as AdditionalData)
        ?.redirect_link;
      if (link) {
        if (link === "post-onboarding") {
          link = "post-onboarding/true";
        }

        // const fullLink = `${appScheme}://${link}`; //commented out as appScheme will be appended in rewriteDeeplink function
        const notificationTitle = getNotificationMessage(
          t,
          notification?.additionalData?.type,
          notification?.additionalData?.view_type,
          notification?.additionalData?.action
        );

        handleRouteChange(client, link);
        showToast(
          notificationTitle || "",
          t("inApp.tapNotification.text"),
          ToastTypes.NOTIFICATION,
          true,
          true,
          undefined,
          () => listener(rewriteDeeplink(link)),
          false,
          7000
        );
      }
    };

    OneSignal.Notifications.addEventListener(
      "foregroundWillDisplay",
      handleForegroundNotification
    );

    OneSignal.Notifications.addEventListener(
      "click",
      handleBackgroundNotification
    );

    return () => {
      urlSubscription.remove();
      OneSignal.Notifications.removeEventListener(
        "click",
        handleBackgroundNotification
      );
      OneSignal.Notifications.removeEventListener(
        "foregroundWillDisplay",
        handleForegroundNotification
      );
    };
  },
  getStateFromPath: (path, options) => {
    const expanded = expandVanityPath(path);
    const state = getStateFromPath(expanded ?? path, options);
    if (expanded && state) {
      setFocusedRoutePath(state, path.replace(/\/$/, ""));
    }
    return reuseLiveStackForHistory(state);
  },
  getPathFromState: (state, config) => {
    const longPath = getPathFromState(state, config);
    const shortPath = shortenSearchResultPath(longPath);
    const finalPath = shortPath ?? longPath;
    if (Platform.OS === "web") {
      return finalPath.startsWith("/webapp")
        ? finalPath
        : `/webapp${finalPath}`;
    }
    return finalPath;
  },
});

export default getLinking;
